import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSave, FiLock, FiCheck, FiAlertCircle } from 'react-icons/fi';
import SEO from '../../components/SEO/SEO';
import { changePassword, clearError, clearSuccessMessage } from '../../redux/slices/authSlice';

const SettingsPage = () => {
  const dispatch = useDispatch();
  const { user, loading, error, successMessage } = useSelector((state) => state.auth);

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    if (successMessage || error) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessMessage());
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, error, dispatch]);

  const handlePasswordChange = (e) => {
    setPasswordForm(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setPasswordError('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('Passwords do not match.');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters long.');
      return;
    }

    try {
      await dispatch(changePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      })).unwrap();

      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      // Error is handled by Redux
    }
  };

  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';

  return (
    <>
      <SEO pageName="profile_settings" noindex={true} />
      <div style={{ background: 'var(--cream-pale)', minHeight: '100vh', color: 'var(--text-dark)', lineHeight: 1.7, fontFamily: "'Jost',sans-serif" }}>
        <style>{`
          .stp-inp{transition:border-color 0.25s}
          .stp-inp:focus{border-color:var(--gold)!important;outline:none}
          .stp-btn{transition:transform 0.25s}
          .stp-btn:hover{transform:translateY(-1px)}
          @media(max-width:768px){.stp-hero{padding:5rem 1.5rem 4rem!important}.stp-body{padding:0 1.2rem 4rem!important}}
        `}</style>

        {/* ── HERO ── */}
        <section className="stp-hero" style={{ background: 'var(--navy)', padding: '6.5rem 3rem 5rem', textAlign: 'center' }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <span className="dv-section-label" style={{ color: 'var(--gold)', display: 'block', marginBottom: '1.3rem' }}>Members Platform</span>
            <h1 className="dv-section-title" style={{ color: 'var(--white)', fontSize: 'clamp(2.6rem, 5vw, 4rem)', fontWeight: 300, lineHeight: 1.06, margin: '0 0 1.1rem' }}>
              Account{' '}<em style={{ fontStyle: 'italic', fontWeight: 300, color: 'var(--cream)' }}>Settings</em>
            </h1>
            <p className="dv-section-lead" style={{ color: 'rgba(232,223,208,0.55)', margin: '0 auto', maxWidth: 480 }}>
              {user?.email}{firstName || lastName ? ` · ${[firstName, lastName].filter(Boolean).join(' ')}` : ''}
            </p>
          </div>
        </section>

        {/* ── BODY ── */}
        <div className="stp-body" style={{ maxWidth: 640, margin: '0 auto', padding: '0 2rem 5rem' }}>
          <div style={{ padding: '3.2rem 0 2.2rem', textAlign: 'center' }}>
            <span className="dv-section-label" style={{ color: 'var(--gold)', display: 'block', marginBottom: '0.7rem', fontSize: '0.68rem' }}>Security</span>
            <h2 className="dv-section-title" style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.5rem)', fontWeight: 400, color: 'var(--navy)', margin: 0 }}>
              Change <strong style={{ fontWeight: 700 }}>Password</strong>
            </h2>
            <div style={{ width: 60, height: 2, background: 'var(--gold)', margin: '1.5rem auto 0' }} />
          </div>

          <AnimatePresence>
            {successMessage && (
              <motion.div
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.25)', borderRadius: '2px', padding: '1rem 1.3rem', marginBottom: '1.2rem' }}
              >
                <FiCheck style={{ width: 18, height: 18, color: '#16a34a', flexShrink: 0 }} />
                <span style={{ fontSize: '0.85rem', color: '#166534' }}>{successMessage}</span>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '2px', padding: '1rem 1.3rem', marginBottom: '1.2rem' }}
              >
                <FiAlertCircle style={{ width: 18, height: 18, color: '#dc2626', flexShrink: 0 }} />
                <span style={{ fontSize: '0.85rem', color: '#991b1b' }}>{error}</span>
              </motion.div>
            )}
          </AnimatePresence>

          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '2px', boxShadow: '0 8px 28px rgba(13,30,46,0.06)' }}>
            <div style={{ padding: '1.6rem 2rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--navy)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <FiLock style={{ width: 17, height: 17, color: 'var(--gold)' }} />
              </div>
              <div>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.35rem', fontWeight: 600, color: 'var(--navy)', margin: 0, lineHeight: 1.2 }}>Update your password</h3>
                <div style={{ width: 40, height: 2, background: 'var(--gold)', marginTop: '0.4rem' }} />
              </div>
            </div>
            <form onSubmit={handlePasswordSubmit} style={{ padding: '2rem' }}>
              {passwordError && (
                <div style={{ background: 'rgba(220,38,38,0.05)', border: '1px solid rgba(220,38,38,0.25)', borderRadius: '2px', padding: '0.9rem 1.1rem', marginBottom: '1.2rem' }}>
                  <p style={{ color: '#991b1b', fontSize: '0.82rem', margin: 0 }}>{passwordError}</p>
                </div>
              )}

              <div style={{ marginBottom: '1.2rem' }}>
                <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-mid)', marginBottom: '0.45rem' }}>Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordChange}
                  className="stp-inp"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '2px', fontSize: '0.9rem', fontFamily: "'Jost',sans-serif", background: 'var(--cream-pale)', color: 'var(--text-dark)' }}
                  required
                />
              </div>
              <div style={{ marginBottom: '1.2rem' }}>
                <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-mid)', marginBottom: '0.45rem' }}>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordChange}
                  className="stp-inp"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '2px', fontSize: '0.9rem', fontFamily: "'Jost',sans-serif", background: 'var(--cream-pale)', color: 'var(--text-dark)' }}
                  required
                  minLength={6}
                />
                <p style={{ fontSize: '0.75rem', fontWeight: 300, color: 'var(--text-light)', margin: '0.45rem 0 0' }}>Minimum 6 characters</p>
              </div>
              <div style={{ marginBottom: '1.8rem' }}>
                <label style={{ display: 'block', fontSize: '0.64rem', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--text-mid)', marginBottom: '0.45rem' }}>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordForm.confirmPassword}
                  onChange={handlePasswordChange}
                  className="stp-inp"
                  style={{ width: '100%', padding: '0.75rem 1rem', border: '1px solid var(--border)', borderRadius: '2px', fontSize: '0.9rem', fontFamily: "'Jost',sans-serif", background: 'var(--cream-pale)', color: 'var(--text-dark)' }}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="stp-btn"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.85rem 2.1rem', background: 'var(--navy)', color: 'var(--white)',
                  border: 'none', borderRadius: '2px', cursor: 'pointer',
                  fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase',
                  fontFamily: "'Jost',sans-serif", opacity: loading ? 0.6 : 1
                }}
              >
                <FiSave style={{ width: 15, height: 15 }} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
